/**
 * 生效地址数据监控列表
 *
 * 一行 = 一个生效目标（target），非上报单。
 * 指标列为 metric_daily 近 7 日聚合；监控结论列取最新一天的 monitor_level（非聚合值）。
 */
import React from 'react';
import {Button, Table, Tag, Tooltip} from 'antd';
import type {ColumnsType} from 'antd/es/table';
import {useTranslation} from 'react-i18next';
import {ExemptTargetType, MonitorLevel, MonitorRow} from '../types/ExemptSpotCheckType';
import {MONITOR_LEVEL_TAG_COLOR} from '../config/exemptSpotCheckConfig';
import {formatCount, renderComplaintCount, renderDeltaRate} from '../utils/metricFormat';
import {usePodExemptDict} from '../../../../hooks/usePodExemptDict';

interface MonitorTableProps {
    rows: MonitorRow[];
    loading: boolean;
    /** 建单提交中：禁用操作列按钮，防连点撞唯一键 */
    creating?: boolean;
    onCreateTask: (row: MonitorRow) => void;
}

const MonitorTable: React.FC<MonitorTableProps> = ({rows, loading, creating, onCreateTask}) => {
    const {t} = useTranslation();
    // 全量字典（含停用）：历史目标的地址类型码停用后仍要能回显
    const {items: addressTypeDictAll} = usePodExemptDict('ADDRESS_TYPE');
    const addressTypeNameOf = (code?: string): string =>
        code ? (addressTypeDictAll.find((item) => item.code === code)?.name ?? code) : '-';

    /** 围栏地址数：物化挂载完成前 addressCount 为空，展示「统计中」 */
    const renderFenceSuffix = (row: MonitorRow): string =>
        String(
            t('exemptSpotCheck.monitor.fenceAddressCount', {
                addressCount: row.addressCount ?? String(t('exemptSpotCheck.monitor.counting')),
            }),
        );

    const columns: ColumnsType<MonitorRow> = [
        {
            title: t('exemptSpotCheck.monitor.columns.targetNo'),
            dataIndex: 'targetNo',
            width: 100,
            align: 'left' as const,
            fixed: 'left',
            render: (targetNo: string) => <span className="text-purple-600">{targetNo}</span>,
        },
        {
            title: t('exemptSpotCheck.monitor.columns.address'),
            dataIndex: 'address',
            width: 260,
            align: 'left' as const,
            render: (address: string, row) => (
                <div>
                    <div className="text-gray-800">
                        {address}
                        {row.targetType === ExemptTargetType.FENCE && (
                            <span className="ml-1 text-gray-500">{renderFenceSuffix(row)}</span>
                        )}
                    </div>
                    <div className="mt-0.5 text-xs text-gray-400">
                        {[row.zipcode, row.warehouseCode].filter(Boolean).join(' · ')}
                    </div>
                </div>
            ),
        },
        {
            title: t('exemptSpotCheck.monitor.columns.addressType'),
            dataIndex: 'addressType',
            width: 140,
            align: 'left' as const,
            render: (code?: string) => addressTypeNameOf(code),
        },
        {
            title: t('exemptSpotCheck.monitor.columns.dsp'),
            dataIndex: 'dspName',
            width: 140,
            align: 'left' as const,
        },
        {
            title: t('exemptSpotCheck.monitor.columns.orderCount7d'),
            dataIndex: 'orderCount7d',
            width: 100,
            align: 'right' as const,
            render: (value?: number) => formatCount(value, String(t('exemptSpotCheck.unit.order'))),
        },
        {
            title: t('exemptSpotCheck.monitor.columns.complaintCount7d'),
            dataIndex: 'complaintCount7d',
            width: 100,
            align: 'right' as const,
            render: (value?: number) => renderComplaintCount(value),
        },
        {
            title: t('exemptSpotCheck.monitor.columns.deltaRate'),
            dataIndex: 'complaintCountDeltaRate',
            width: 110,
            align: 'right' as const,
            render: (value?: number) => renderDeltaRate(value),
        },
        {
            title: t('exemptSpotCheck.monitor.columns.monitorLevel'),
            dataIndex: 'monitorLevel',
            width: 220,
            align: 'left' as const,
            render: (level: MonitorLevel | undefined, row) => (
                <div>
                    {level ? (
                        <Tag color={MONITOR_LEVEL_TAG_COLOR[level]}>
                            {t(`exemptSpotCheck.monitorLevel.${level}`)}
                        </Tag>
                    ) : (
                        <span className="text-gray-300">-</span>
                    )}
                    {row.triggerDetail && (
                        <div className="mt-1 text-xs text-gray-500">{row.triggerDetail}</div>
                    )}
                </div>
            ),
        },
        {
            title: t('exemptSpotCheck.columns.action'),
            key: 'action',
            width: 110,
            align: 'left' as const,
            fixed: 'right',
            // 趋势图（原型有此按钮）产品确认不做，故操作列仅保留「建抽查任务」
            render: (_, row) => {
                const button = (
                    <Button
                        type="link"
                        size="small"
                        className="px-0"
                        // 同周期同目标有唯一键，连点第二次必然报「已存在」，提交中直接禁用
                        disabled={creating || row.hasSpotTaskInPeriod}
                        onClick={() => onCreateTask(row)}
                    >
                        {row.hasSpotTaskInPeriod
                            ? t('exemptSpotCheck.monitor.taskCreatedInPeriod')
                            : t('exemptSpotCheck.monitor.createTask')}
                    </Button>
                );
                // 禁用态的 Button 不触发鼠标事件，Tooltip 须套在外层元素上才能显示
                return row.hasSpotTaskInPeriod ? (
                    <Tooltip title={t('exemptSpotCheck.monitor.taskCreatedTooltip')}>
                        <span>{button}</span>
                    </Tooltip>
                ) : (
                    button
                );
            },
        },
    ];

    /** 高风险行标红；需关注不标，避免整屏都是底色反而看不出轻重 */
    const rowClassName = (row: MonitorRow) =>
        row.monitorLevel === MonitorLevel.HIGH_RISK ? 'pod-exempt-monitor-high-risk-row' : '';

    return (
        <>
            {/* 背景色写在 td 上并加 !important：antd 固定列的单元格自带背景，加在 tr 上会被盖住 */}
            <style>{`
                .pod-exempt-monitor-high-risk-row td {
                    background-color: #fef2f2 !important;
                }
                .pod-exempt-monitor-high-risk-row:hover td {
                    background-color: #fee2e2 !important;
                }
            `}</style>
            <Table<MonitorRow>
                rowKey={(row) => `${row.targetType}-${row.targetId}`}
                columns={columns}
                dataSource={rows}
                loading={loading}
                pagination={false}
                scroll={{x: 1600}}
                size="small"
                rowClassName={rowClassName}
            />
        </>
    );
};

export default MonitorTable;
